import React, { useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { AxiosError, AxiosResponse } from "axios";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { uploadFieldServiceSoftware } from "@/api/uploadFieldServiceSoftware/uploadFieldServiceSoftwareApi";
import Gleap from "gleap";
import { HelpCircle, Cloud, Settings } from "lucide-react";
import { useNotification } from "@/context/NotificationContext";
import { useSubscription } from "@apollo/client";
import { useSelector } from "react-redux";
import { RootState } from "@/app/rootReducer";
import ConfirmationDialog from "@/components/ConfirmationDialog/ConfirmationDialog";
import { ON_MEMBERS_STREAM_COUNT_SUBSCRIPTION } from "@/modules/stochastic/features/FieldServiceImport/graphql/subscriptions/onMembersStreamCount";
import { MembersStreamCountSubscriptionData } from "@/modules/stochastic/features/FieldServiceImport/graphql/subscriptions/onMembersStreamCount/types";
import { ON_COMPANY_DATA_IMPORT_JOB_IN_PROGRESS_COUNT_SUBSCRIPTION } from "@/modules/stochastic/features/CampaignManagement/graphql/subscriptions/onCompanyDataImportJobInProgressCount";
import { CompanyDataImportJobInProgressCountSubscriptionData } from "@/modules/stochastic/features/CampaignManagement/graphql/subscriptions/types";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { validateServiceTitanBusinessUnitMappings } from "@/api/validateServiceTitanBusinessUnitMappings/validateServiceTitanBusinessUnitMappingsApi";
import { Link } from "react-router-dom";
import { ValidateServiceTitanBusinessUnitMappingsResponse } from "@/api/validateServiceTitanBusinessUnitMappings/types";
import { fetchServiceTitanCredential } from "@/api/fetchServiceTitanCredential/fetchServiceTitanCredentialApi";
import { ServiceTitanCredential } from "@/api/fetchServiceTitanCredential/types";
import { triggerServiceTitanImport } from "@/api/triggerServiceTitanImport/triggerServiceTitanImportApi";
import { Button } from "@/components/ui/button";
import { usePurgeInvoices } from "@/modules/stochastic/features/PurgeInvoices/hooks/usePurgeInvoices";
import PurgeInvoicesModal from "../PurgeInvoicesModal/PurgeInvoicesModal";
import permissionsService from "@/services/permissionsService";

const fileTypes = ["CSV", "XLSX", "XLS"];
const softwarePackages = [
  { value: "ServiceTitan", label: "ServiceTitan" },
  { value: "Other", label: "Other" },
];
const importTypes = [
  {
    value: "activeCustomers",
    label: "Active Customers (Include ALL customers)",
    description: null,
  },
  {
    value: "invoices",
    label: "Invoices (Include invoices since last upload)",
    description: null,
  },
];

interface TradeOption {
  label: string;
  value: string;
}

interface FieldServiceImportProps {
  supportedTrades?: TradeOption[];
  onUploadComplete?: (jobId: string) => void;
}

export const FieldServiceImport: React.FC<FieldServiceImportProps> = ({
  supportedTrades = [
    { label: "HVAC", value: "hvac" },
    { label: "Electrical", value: "electrical" },
    { label: "Plumbing", value: "plumbing" },
    { label: "Roofing", value: "roofing" },
  ],
}) => {
  const { showNotification } = useNotification();
  const { hasPermission } = permissionsService();
  const userAppSettings = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings,
  );
  const tenantId = userAppSettings?.intacctId;
  const companyId = userAppSettings?.companyId ?? 0;
  const { handlePurgeInvoices, purgeLoading } = usePurgeInvoices();

  // Check if user can manage ServiceTitan integration
  const canManageServiceTitan = hasPermission("servicetitan-integration");
  const canPurgeInvoices = hasPermission("stochastic-purge-invoices");

  const { data: memberStreamData, error: memberStreamError } =
    useSubscription<MembersStreamCountSubscriptionData>(
      ON_MEMBERS_STREAM_COUNT_SUBSCRIPTION,
      {
        variables: { tenantId: tenantId ?? "" },
        skip: !tenantId,
      },
    );

  const { data: importData } =
    useSubscription<CompanyDataImportJobInProgressCountSubscriptionData>(
      ON_COMPANY_DATA_IMPORT_JOB_IN_PROGRESS_COUNT_SUBSCRIPTION,
      {
        variables: { companyId },
        skip: !companyId,
      },
    );

  const inProgressCount =
    importData?.company_data_import_job_aggregate?.aggregate?.count ?? 0;
  const isImportInProgress = inProgressCount > 0;

  useEffect(() => {
    if (memberStreamData) {
      console.log(
        "Subscription data for members_stream count:",
        memberStreamData,
      );
    }
  }, [memberStreamData]);

  useEffect(() => {
    if (memberStreamError) {
      console.error(
        "Members stream count subscription error:",
        memberStreamError.message,
      );
    }
  }, [memberStreamError]);

  const [selectedSoftware, setSelectedSoftware] = useState("ServiceTitan");
  const [selectedTrade, setSelectedTrade] = useState(
    supportedTrades.length === 1 ? supportedTrades[0].value : "",
  );
  const [selectedImportType, setSelectedImportType] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [, setSelectedFile] = useState<File | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isApiImporting, setIsApiImporting] = useState(false);
  const [serviceTitanCredential, setServiceTitanCredential] =
    useState<ServiceTitanCredential | null>(null);
  const [checkingCredentials, setCheckingCredentials] = useState(false);
  const [showPurgeConfirmationModal, setShowPurgeConfirmationModal] =
    useState(false);

  const [dialogTitleContent, setDialogTitleContent] =
    useState<React.ReactNode>("");
  const [dialogInstructionTitle, setDialogInstructionTitle] =
    useState<React.ReactNode>("");
  const [dialogInstructionItems, setDialogInstructionItems] = useState<
    string[]
  >([]);
  const [dialogInstructionFinalQuestion, setDialogInstructionFinalQuestion] =
    useState<string>("");
  const [cancelMessage, setCancelMessage] = useState<string>("");
  const [confirmMessage, setConfirmMessage] = useState<string>("");
  const [cancelHandleFunction, setCancelHandleFunction] = useState<() => void>(
    () => () => {},
  );
  const [confirmHandleFunction, setConfirmHandleFunction] = useState<
    () => void
  >(() => () => {});

  // Check for ServiceTitan credentials when ServiceTitan is selected
  useEffect(() => {
    const checkServiceTitanCredentials = async () => {
      if (selectedSoftware === "ServiceTitan") {
        // Skip API call if user doesn't have permission
        if (!canManageServiceTitan) {
          setServiceTitanCredential(null);
          return;
        }

        const companyUuid = localStorage.getItem("selectedCompanyUuid");
        if (!companyUuid) {
          // Set to null if no company UUID to show the configuration UI
          setServiceTitanCredential(null);
          return;
        }

        setCheckingCredentials(true);
        try {
          const response = await fetchServiceTitanCredential(companyUuid);

          if (response.data && response.data.credential) {
            setServiceTitanCredential(response.data.credential);
          } else {
            setServiceTitanCredential(null);
          }
        } catch {
          setServiceTitanCredential(null);
        } finally {
          setCheckingCredentials(false);
        }
      } else {
        // Reset state when not ServiceTitan
        setServiceTitanCredential(null);
      }
    };

    checkServiceTitanCredentials();
  }, [selectedSoftware, canManageServiceTitan]);

  const handleUploadButton = async () => {
    const isTradeRequired = supportedTrades.length > 1;

    if (
      !file ||
      !selectedSoftware ||
      !selectedImportType ||
      (isTradeRequired && !selectedTrade)
    ) {
      const message = "Please fill in all required fields";
      alert(message);
      showNotification("Missing Fields", message, "error");
      return;
    }

    // Check if ServiceTitan business units are mapped to trades
    if (selectedSoftware === "ServiceTitan") {
      try {
        const companyUuid = localStorage.getItem("selectedCompanyUuid");
        if (!companyUuid) {
          showNotification("Error", "Company information not found", "error");
          return;
        }

        const validationResult =
          (await validateServiceTitanBusinessUnitMappings(
            companyUuid,
          )) as ValidateServiceTitanBusinessUnitMappingsResponse;
        console.log("validationResult full response:", validationResult);

        // Handle the response structure
        const isValid = validationResult?.isValid ?? true;
        const unmappedCount = validationResult?.unmappedCount ?? 0;

        if (!isValid) {
          const editCompanyPath = `/admin/companies/${companyUuid}/edit`;

          const errorMessage = (
            <div>
              {unmappedCount > 0 ? (
                <>
                  You have {unmappedCount} unmapped ServiceTitan Business Unit
                  {unmappedCount !== 1 ? "s" : ""}. You must map each
                  ServiceTitan Business Unit to a Trade before uploading.{" "}
                  <Link
                    className="text-blue-600 hover:text-blue-800 underline"
                    onClick={() => setShowConfirmationModal(false)}
                    state={{ defaultTab: "servicetitan" }}
                    to={editCompanyPath}
                  >
                    Click here to go to Company Management
                  </Link>{" "}
                  and click on the ServiceTitan tab to complete the mapping.
                </>
              ) : (
                <>
                  All ServiceTitan Business Units must be mapped to a Trade
                  uploading.{" "}
                  <Link
                    className="text-blue-600 hover:text-blue-800 underline"
                    onClick={() => setShowConfirmationModal(false)}
                    state={{ defaultTab: "servicetitan" }}
                    to={editCompanyPath}
                  >
                    Click here to go to Company Management
                  </Link>{" "}
                  and click on the ServiceTitan tab to complete the mapping.
                </>
              )}
            </div>
          );

          setDialogTitleContent("Business Unit Mapping Required");
          setDialogInstructionTitle(errorMessage);
          setDialogInstructionItems([]);
          setDialogInstructionFinalQuestion("");
          setCancelMessage("Close");
          setConfirmMessage("");
          setCancelHandleFunction(() => handleUploadCancelModal);
          setConfirmHandleFunction(() => () => {});
          setShowConfirmationModal(true);
          return;
        }
      } catch (error) {
        console.error("Error validating business unit mappings:", error);
        showNotification(
          "Error",
          "Failed to validate business unit mappings",
          "error",
        );
        return;
      }
    }

    if (selectedImportType === "activeCustomers") {
      setDialogTitleContent("Upload Active Customers");
      setDialogInstructionTitle(
        "You are about to upload a new customer list. This will:",
      );
      setDialogInstructionItems([
        "Mark all existing customers as inactive",
        "Set only the customers in your new list as active",
        "Preserve all historical invoice data",
      ]);
      setDialogInstructionFinalQuestion("Are you sure you want to proceed?");
      setConfirmMessage("Confirm Upload");
      setCancelMessage("Cancel");
      setCancelHandleFunction(() => handleUploadCancelModal);
      setConfirmHandleFunction(() => handleUploadConfirmModal);
      setShowConfirmationModal(true);
    } else {
      doActualUpload();
    }
  };

  const doActualUpload = async () => {
    if (!file) return;
    setUploadError(null);
    setUploadSuccess(false);
    setSelectedFile(file);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      await uploadFieldServiceSoftware(
        {
          file,
          software: selectedSoftware,
          trade: selectedTrade,
          importType: selectedImportType,
        },
        (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          setUploadProgress(percentCompleted);
        },
      );

      setUploadSuccess(true);
      setFile(null);
      setSelectedSoftware("");
      setSelectedTrade("");
      setSelectedImportType("");
      setSelectedFile(null);
      showNotification("Success", `File successfully uploaded!`, "success");
    } catch (err) {
      let errorMessage = "An error occurred.";
      try {
        const typedError = err as AxiosError;
        if (typedError.response) {
          errorMessage = await extractErrorMessage(
            typedError.response as AxiosResponse,
          );
        }
      } catch (parseErr) {
        console.error("Error parsing upload error:", parseErr);
      }
      setUploadError(errorMessage);
      setDialogTitleContent("Upload Error");
      setDialogInstructionTitle(errorMessage);
      setDialogInstructionItems([]);
      setDialogInstructionFinalQuestion("");
      setCancelMessage("Close");
      setConfirmMessage("");
      setCancelHandleFunction(() => handleUploadCancelModal);
      setConfirmHandleFunction(() => () => {});
      setShowConfirmationModal(true);
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (isUploading || uploadSuccess || uploadError) {
      document
        .getElementById("upload-status")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isUploading, uploadSuccess, uploadError]);

  const handleUploadConfirmModal = async () => {
    setShowConfirmationModal(false);
    await doActualUpload();
  };

  const handleUploadCancelModal = () => {
    setShowConfirmationModal(false);
  };

  const handleApiImport = async () => {
    // Check if ServiceTitan business units are mapped to trades
    try {
      const companyUuid = localStorage.getItem("selectedCompanyUuid");
      if (!companyUuid) {
        showNotification("Error", "Company information not found", "error");
        return;
      }

      const validationResult = (await validateServiceTitanBusinessUnitMappings(
        companyUuid,
      )) as ValidateServiceTitanBusinessUnitMappingsResponse;
      console.log("validationResult full response:", validationResult);

      // Handle the response structure
      const isValid = validationResult?.isValid ?? true;
      const unmappedCount = validationResult?.unmappedCount ?? 0;

      if (!isValid) {
        const editCompanyPath = `/admin/companies/${companyUuid}/edit`;

        const errorMessage = (
          <div>
            {unmappedCount > 0 ? (
              <>
                You have {unmappedCount} unmapped ServiceTitan Business Unit
                {unmappedCount !== 1 ? "s" : ""}. You must map each ServiceTitan
                Business Unit to a Trade before importing.{" "}
                <Link
                  className="text-blue-600 hover:text-blue-800 underline"
                  onClick={() => setShowConfirmationModal(false)}
                  state={{ defaultTab: "servicetitan" }}
                  to={editCompanyPath}
                >
                  Click here to go to Company Management
                </Link>{" "}
                and click on the ServiceTitan tab to complete the mapping.
              </>
            ) : (
              <>
                All ServiceTitan Business Units must be mapped to a Trade before
                importing.{" "}
                <Link
                  className="text-blue-600 hover:text-blue-800 underline"
                  onClick={() => setShowConfirmationModal(false)}
                  state={{ defaultTab: "servicetitan" }}
                  to={editCompanyPath}
                >
                  Click here to go to Company Management
                </Link>{" "}
                and click on the ServiceTitan tab to complete the mapping.
              </>
            )}
          </div>
        );

        setDialogTitleContent("Business Unit Mapping Required");
        setDialogInstructionTitle(errorMessage);
        setDialogInstructionItems([]);
        setDialogInstructionFinalQuestion("");
        setCancelMessage("Close");
        setConfirmMessage("");
        setCancelHandleFunction(() => handleUploadCancelModal);
        setConfirmHandleFunction(() => () => {});
        setShowConfirmationModal(true);
        return;
      }
    } catch (error) {
      console.error("Error validating business unit mappings:", error);
      showNotification(
        "Error",
        "Failed to validate business unit mappings",
        "error",
      );
      return;
    }

    // Proceed with API import
    setIsApiImporting(true);
    try {
      const companyUuid = localStorage.getItem("selectedCompanyUuid");
      if (!companyUuid) {
        showNotification("Error", "Company information not found", "error");
        return;
      }

      // Check if we have a credential
      if (!serviceTitanCredential) {
        showNotification("Error", "ServiceTitan credential not found", "error");
        return;
      }

      showNotification(
        "Info",
        "Starting ServiceTitan data import...",
        "success",
      );

      const syncResponse = await triggerServiceTitanImport(companyUuid, {
        credentialId: serviceTitanCredential.id,
        syncType: "manual",
        syncDataType: "both", // Import both customers and invoices
      });

      if (syncResponse?.success) {
        showNotification(
          "Success",
          syncResponse.message ||
            "ServiceTitan data import initiated successfully!",
          "success",
        );
        setSelectedImportType("");
      } else {
        showNotification(
          "Error",
          syncResponse?.message ||
            "Failed to initiate ServiceTitan data import",
          "error",
        );
      }
    } catch (error) {
      console.error("API import error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to initiate ServiceTitan sync";
      showNotification("Error", errorMessage, "error");
    } finally {
      setIsApiImporting(false);
    }
  };

  const handlePurgeConfirmModal = async () => {
    setShowPurgeConfirmationModal(false);
    if (tenantId) {
      await handlePurgeInvoices(tenantId);
    }
  };

  const handlePurgeCancelModal = () => {
    setShowPurgeConfirmationModal(false);
  };

  return (
    <MainPageWrapper
      actions={
        canPurgeInvoices ? (
          <Button
            disabled={!tenantId || purgeLoading || isImportInProgress}
            onClick={() => {
              setShowPurgeConfirmationModal(true);
            }}
          >
            {purgeLoading ? "Purging..." : "Purge Imported Invoices"}
          </Button>
        ) : null
      }
      title="Field Service Import"
      titleHelpIcon={
        <HelpCircle
          className="w-5 h-5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer"
          onClick={() => Gleap.openHelpCenterCollection("3", false)}
        />
      }
    >
      <div>
        <div className="mb-4">
          <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
            Software Package
          </label>
          <select
            className="p-2 border rounded w-72 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
            onChange={(e) => setSelectedSoftware(e.target.value)}
            value={selectedSoftware}
          >
            <option value="">Select software package</option>
            {softwarePackages.map((software) => (
              <option key={software.value} value={software.value}>
                {software.label}
              </option>
            ))}
          </select>
        </div>

        {supportedTrades.length > 1 && (
          <div className="mb-4">
            <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">Trade</label>
            <select
              className="p-2 border rounded w-72 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100"
              onChange={(e) => setSelectedTrade(e.target.value)}
              value={selectedTrade}
            >
              <option value="">Select trade</option>
              {supportedTrades.map((trade) => (
                <option key={trade.value} value={trade.value}>
                  {trade.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedSoftware === "ServiceTitan" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Section - Import Type & File Upload */}
            <div className="p-6 border border-gray-200 dark:border-slate-700 rounded-lg bg-gray-50 dark:bg-slate-800">
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Import Type
                </label>
                {importTypes.map((type) => (
                  <div className="mb-2" key={type.value}>
                    <label className="flex items-start">
                      <input
                        checked={selectedImportType === type.value}
                        className="mt-1 mr-2"
                        name="importType"
                        onChange={(e) => setSelectedImportType(e.target.value)}
                        type="radio"
                        value={type.value}
                      />
                      <div>
                        <div className="text-sm dark:text-gray-300">{type.label}</div>
                        {type.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {type.description}
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                ))}
              </div>

              <div className="mb-4">
                <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">File</label>
                <FileUploader
                  handleChange={(f: File) => setFile(f)}
                  maxSize={100}
                  name="file"
                  types={fileTypes}
                  uploadedLabel="Uploaded Successfully!"
                />
                {file && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Selected file: {file.name}
                  </p>
                )}
              </div>
              <button
                className="w-full mt-2 p-2 bg-blue-500 text-white rounded disabled:bg-gray-300 hover:bg-blue-600 transition-colors"
                disabled={!file || isUploading || !selectedImportType}
                onClick={handleUploadButton}
              >
                {isUploading
                  ? "Uploading and Processing: Please wait ..."
                  : "Upload File"}
              </button>
            </div>

            {/* Right Section - ServiceTitan API Import (only shown if user has permission) */}
            {canManageServiceTitan && (
              <>
                {checkingCredentials ? (
                  <div className="p-6 border border-gray-200 dark:border-slate-700 rounded-lg bg-gray-50 dark:bg-slate-800">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                    <p className="text-center mt-4 text-gray-600 dark:text-gray-400">
                      Checking ServiceTitan credentials...
                    </p>
                  </div>
                ) : serviceTitanCredential === null ? (
                  // Show configure prompt when no credentials exist
                  <div className="p-6 border border-gray-200 dark:border-slate-700 rounded-lg bg-gray-50 dark:bg-slate-800">
                    <div className="flex items-center justify-center mb-4">
                      <Cloud className="w-16 h-16 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white text-center mb-2">
                      ServiceTitan integration not configured
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                      Connect your ServiceTitan account to enable automatic data
                      synchronization
                    </p>
                    <button
                      className="w-full p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
                      onClick={() => {
                        try {
                          const companyUuid = localStorage.getItem(
                            "selectedCompanyUuid",
                          );
                          const companyName = localStorage.getItem(
                            "selectedCompanyName",
                          );
                          const userEmail = localStorage.getItem("userEmail");

                          // Pre-fill form with company context
                          if (
                            typeof Gleap !== "undefined" &&
                            Gleap.preFillForm
                          ) {
                            Gleap.preFillForm({
                              companyId: companyUuid || "",
                              companyName: companyName || "",
                              userEmail: userEmail || "",
                              integrationType: "ServiceTitan",
                              requestType: "configure_integration",
                              tenantId: tenantId || "",
                              subject:
                                "ServiceTitan Integration Configuration Request",
                            });
                          }

                          // Attach metadata to the form submission
                          if (
                            typeof Gleap !== "undefined" &&
                            Gleap.attachCustomData
                          ) {
                            Gleap.attachCustomData({
                              formType: "serviceTitanIntegration",
                              companyUuid: companyUuid || "",
                              tenantId: tenantId || "",
                              timestamp: new Date().toISOString(),
                              source: "field_service_import_page",
                            });
                          }

                          // Open the ServiceTitan configuration form
                          if (
                            typeof Gleap !== "undefined" &&
                            Gleap.startClassicForm
                          ) {
                            Gleap.startClassicForm(
                              "d13a831fd9bc4715b310",
                              false,
                            );
                          } else {
                            // Fallback to general widget if method not available
                            Gleap.open();
                          }
                        } catch (error) {
                          showNotification(
                            "Configuration Form Error",
                            "Unable to open the configuration form. Please try again or contact support.",
                            "error",
                          );
                        }
                      }}
                    >
                      <Settings className="w-5 h-5 mr-2" />
                      Configure ServiceTitan Integration
                    </button>
                  </div>
                ) : (
                  // Show API Import section when credentials exist
                  <div className="p-6 border border-gray-200 dark:border-slate-700 rounded-lg bg-gray-50 dark:bg-slate-800">
                    <div className="flex items-center mb-4">
                      <Cloud className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Import from ServiceTitan API
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Directly import data from your ServiceTitan account using
                      the API connection. This will use your configured
                      ServiceTitan credentials to fetch the latest data.
                    </p>
                    <button
                      className="w-full mt-auto p-2 bg-green-500 text-white rounded disabled:bg-gray-300 hover:bg-green-600 transition-colors"
                      disabled={isApiImporting}
                      onClick={handleApiImport}
                    >
                      {isApiImporting
                        ? "Importing from API: Please wait ..."
                        : "Import from API"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                Import Type
              </label>
              {importTypes.map((type) => (
                <div className="mb-2" key={type.value}>
                  <label className="flex items-start">
                    <input
                      checked={selectedImportType === type.value}
                      className="mt-1 mr-2"
                      name="importType"
                      onChange={(e) => setSelectedImportType(e.target.value)}
                      type="radio"
                      value={type.value}
                    />
                    <div>
                      <div className="text-sm dark:text-gray-300">{type.label}</div>
                      {type.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {type.description}
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              ))}
            </div>

            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">File</label>
              <FileUploader
                handleChange={(f: File) => setFile(f)}
                maxSize={100}
                name="file"
                types={fileTypes}
                uploadedLabel="Uploaded Successfully!"
              />
              {file && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Selected file: {file.name}
                </p>
              )}
            </div>

            <button
              className="mt-2 p-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
              disabled={!file || isUploading || !selectedImportType}
              onClick={handleUploadButton}
            >
              {isUploading
                ? "Uploading and Processing: Please wait ..."
                : "Upload"}
            </button>
          </>
        )}

        <div className="mb-4" id="upload-status">
          {isUploading && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                <div
                  className="bg-blue-500 h-2.5 rounded-full"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {uploadProgress}%
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmationDialog
        cancelMessage={cancelMessage}
        confirmMessage={confirmMessage}
        dialogInstructionFinalQuestion={dialogInstructionFinalQuestion}
        dialogInstructionItems={dialogInstructionItems}
        dialogInstructionTitle={dialogInstructionTitle}
        dialogTitleContent={dialogTitleContent}
        isOpen={showConfirmationModal}
        onClose={cancelHandleFunction}
        onConfirm={confirmHandleFunction}
      />

      <PurgeInvoicesModal
        isOpen={showPurgeConfirmationModal}
        isPurging={purgeLoading}
        onClose={handlePurgeCancelModal}
        onConfirm={handlePurgeConfirmModal}
      />
    </MainPageWrapper>
  );
};
